import React, { useEffect, useState } from 'react';
import { Button, Form, Spin } from 'antd';
import { message } from '../services/toastMessage';
import { registerUser, checkEmailExists } from '../services/AuthenticationService';
import { IRegisterForm } from '../interfaces/forms/IRegisterForm';
import { PENDING_AGE_VERIFICATION_KEY } from '../services/AgeVerificationService';
import NotAuthenticatedLayout from './Layouts/NotAuthenticatedLayout';
import ConfirmationPendingNotice from './ConfirmationPendingNotice';
import FloatingLabelInput from './FloatingLabelInput';
import { translate } from '../helpers/translationHelper';
import { TRANSLATIONS } from '../constants/translations';
import { useTranslation } from 'react-i18next';
import OAuthButtons from './../components/OAuth/OAuthButtons';
import { TEAL } from '../theme/creamyTokens';
import { useAppBreakpoint } from '../hooks/useAppBreakpoint';

const RegistrationForm: React.FC = () => {
  const { t } = useTranslation();
  const { isMobile } = useAppBreakpoint();
  const [registering, setRegistering] = useState<boolean>(false);
  const [email, setEmail] = useState<string>('');
  const [emailExists, setEmailExists] = useState<boolean | null>(null);
  const [checkingEmail, setCheckingEmail] = useState<boolean>(false);
  // Set once registration succeeds -- swaps the form for the "check your
  // email" card instead of navigating away, since registering no longer
  // signs the user in (see AuthController.Register's own comment: Login
  // now refuses unconfirmed accounts, so handing back a working token
  // here would just let them skip that gate entirely).
  const [registeredEmail, setRegisteredEmail] = useState<string | null>(null);

  useEffect(() => {
    if (!email) {
      setEmailExists(null);
      return;
    }

    const timer = setTimeout(async () => {
      setCheckingEmail(true);
      try {
        const response = await checkEmailExists(email);
        setEmailExists(response);
      } catch (error) {
        setEmailExists(null);
      } finally {
        setCheckingEmail(false);
      }
    }, 800);

    return () => clearTimeout(timer);
  }, [email]);

  const onFinish = async (values: IRegisterForm) => {
    setRegistering(true);
    try {
      const response = await registerUser(
        values.email,
        values.password
      );

      if (response.requireAgeVerification) {
        localStorage.setItem(PENDING_AGE_VERIFICATION_KEY, 'true');
      }
      setRegisteredEmail(values.email);
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.REGISTRATION_ERROR_FALLBACK));
    } finally {
      setRegistering(false);
    }
  };

  if (registeredEmail) {
    return (
      <NotAuthenticatedLayout>
        <ConfirmationPendingNotice
          email={registeredEmail}
          title={translate(t, TRANSLATIONS.REGISTER_CHECK_EMAIL_TITLE)}
          description={translate(t, TRANSLATIONS.REGISTER_CHECK_EMAIL_DESCRIPTION)}
        />
      </NotAuthenticatedLayout>
    );
  }

  return (
    <NotAuthenticatedLayout>
      <div style={{
        maxWidth: 400,
        margin: isMobile ? '12px auto' : '50px auto',
        width: '100%',
        padding: isMobile ? 0 : '0 16px',
      }}>
        <h1 style={{ marginBottom: 24 }}>{translate(t, TRANSLATIONS.REGISTER)}</h1>

        <Spin spinning={registering}>
          <Form layout="vertical" onFinish={onFinish}>
            <Form.Item
              name="email"
              rules={[
                { required: true, message: translate(t, TRANSLATIONS.EMAIL_MESSAGE) },
                { type: 'email', message: translate(t, TRANSLATIONS.EMAIL_VALID_MESSAGE) },
              ]}
              validateStatus={emailExists === false ? 'success' : emailExists === true ? 'error' : ''}
              help={emailExists === true ? translate(t, TRANSLATIONS.REGISTRATION_EMAIL_EXISTS_HELP) : ''}
            >
              <FloatingLabelInput
                label={translate(t, TRANSLATIONS.EMAIL)}
                disabled={registering}
                onChange={(e) => setEmail(e.target.value)}
                // Always the same suffix node (never toggling to/from null):
                // antd's Input swaps between a plain <input> and one wrapped
                // in .ant-input-affix-wrapper depending on whether it *has* a
                // suffix at all, so flipping presence remounts the underlying
                // <input> and drops focus every time a check finishes.
                suffix={<Spin size="small" style={{ opacity: checkingEmail ? 1 : 0 }} />}
                style={{ borderRadius: 999 }}
              />
            </Form.Item>

            <Form.Item
              name="password"
              rules={[{ required: true, message: translate(t, TRANSLATIONS.PASSWORD_MESSAGE) }]}
            >
              <FloatingLabelInput label={translate(t, TRANSLATIONS.PASSWORD)} password disabled={registering} style={{ borderRadius: 999 }} />
            </Form.Item>

            <Form.Item
              name="confirmPassword"
              dependencies={['password']}
              rules={[
                { required: true, message: translate(t, TRANSLATIONS.CONFIRM_PASSWORD_MESSAGE) },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error(translate(t, TRANSLATIONS.PASSWORDS_NOT_MATCH_MESSAGE)));
                  },
                }),
              ]}
            >
              <FloatingLabelInput label={translate(t, TRANSLATIONS.CONFIRM_PASSWORD)} password disabled={registering} style={{ borderRadius: 999 }} />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                block
                disabled={registering}
                style={{ borderRadius: 999, height: 48 }}
              >
                {registering ? translate(t, TRANSLATIONS.REGISTERING) : translate(t, TRANSLATIONS.REGISTER)}
              </Button>
            </Form.Item>
          </Form>

          <OAuthButtons showDivider={true} iconOnly />
        </Spin>

        <div style={{ marginTop: 20, textAlign: 'center', fontSize: 14 }}>
          <span style={{ color: 'rgba(0, 0, 0, 0.45)' }}>{translate(t, TRANSLATIONS.REGISTRATION_ALREADY_HAVE_ACCOUNT)} </span>
          <a href="/login" style={{ color: TEAL, fontWeight: 500 }}>
            {translate(t, TRANSLATIONS.REGISTRATION_LOGIN_HERE_LINK)}
          </a>
        </div>
      </div>
    </NotAuthenticatedLayout>
  );
};

export default RegistrationForm;
